import { cn } from '#app/utils/misc'

/**
 * BlogBlock - Featured blog posts grid
 *
 * This component renders a grid of blog posts with images, titles, and excerpts.
 * Design reference: TWBlocks Blog patterns
 *
 * Features:
 * - Responsive grid layout (1-3 columns)
 * - Post image, title, excerpt, date
 * - Read more links
 * - Featured post support
 */

export interface BlogPost {
	title?: string
	excerpt?: string
	date?: string
	image?: string
	link?: string
	author?: string
}

export interface BlogBlockProps {
	title?: string
	subtitle?: string
	columns?: 1 | 2 | 3
	posts?: BlogPost[]
}

const columnClasses = {
	1: 'md:grid-cols-1',
	2: 'md:grid-cols-2',
	3: 'md:grid-cols-2 lg:grid-cols-3',
}

export function BlogBlock({
	title = 'Latest from Our Blog',
	subtitle = 'Stay up to date with our latest articles',
	columns = 3,
	posts = [],
}: BlogBlockProps) {
	// Default posts if none provided
	const displayPosts: BlogPost[] =
		posts.length > 0
			? posts
			: [
					{
						title: '10 Tips for Productivity',
						excerpt:
							'Discover proven strategies to boost your productivity and get more done in less time.',
						date: 'March 15, 2024',
						link: '/blog/productivity-tips',
						author: 'Sarah Johnson',
					},
					{
						title: 'Getting Started Guide',
						excerpt:
							'A comprehensive guide to help you get started with our platform in just 5 minutes.',
						date: 'March 10, 2024',
						link: '/blog/getting-started',
						author: 'Michael Chen',
					},
					{
						title: 'Product Updates Q1 2024',
						excerpt:
							'Exciting new features and improvements we shipped in the first quarter of 2024.',
						date: 'March 5, 2024',
						link: '/blog/q1-updates',
						author: 'Emily Davis',
					},
				]

	return (
		<section className="w-full py-16 md:py-24">
			<div className="container px-4 md:px-6">
				{/* Header */}
				<div className="mb-12 flex flex-col items-center text-center">
					{title && <h2 className="text-title-2 mb-4">{title}</h2>}
					{subtitle && (
						<p className="text-muted-foreground max-w-[700px] text-lg md:text-xl">
							{subtitle}
						</p>
					)}
				</div>

				{/* Blog Posts Grid */}
				<div
					className={cn(
						'grid gap-8',
						columnClasses[columns] || columnClasses[3],
					)}
				>
					{displayPosts.map((post, index) => (
						<article
							key={index}
							className="bg-card flex flex-col overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md"
						>
							{/* Post Image */}
							{post.image && (
								<div className="aspect-video w-full overflow-hidden">
									<img
										src={post.image}
										alt={post.title || 'Blog post'}
										className="h-full w-full object-cover transition-transform hover:scale-105"
									/>
								</div>
							)}

							{/* Post Content */}
							<div className="flex flex-1 flex-col gap-3 p-6">
								{/* Date & Author */}
								<div className="text-muted-foreground flex items-center gap-2 text-sm">
									{post.date && <time>{post.date}</time>}
									{post.date && post.author && <span>•</span>}
									{post.author && <span>{post.author}</span>}
								</div>

								{/* Title */}
								{post.title && (
									<h3 className="text-xl leading-tight font-semibold">
										<a
											href={post.link || '#'}
											className="hover:text-primary transition-colors"
										>
											{post.title}
										</a>
									</h3>
								)}

								{/* Excerpt */}
								{post.excerpt && (
									<p className="text-muted-foreground flex-1 text-sm">
										{post.excerpt}
									</p>
								)}

								{/* Read More Link */}
								{post.link && (
									<a
										href={post.link}
										className="text-primary hover:text-foreground font-medium transition-colors"
									>
										Read more →
									</a>
								)}
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	)
}
